import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../supabaseClient';
import { User } from '@supabase/supabase-js';
import { API_BASE_URL } from '../config/api';

interface AccountUser {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: 'owner' | 'admin' | 'member';
  joined_at: string;
  invited_by?: string;
  is_active: boolean;
  avatar_url?: string;
}

interface Account {
  account_id: string;
  account_name: string;
  user_role: 'owner' | 'admin' | 'member';
  plan_type: string;
  plan_status: string;
  is_account_active: boolean;
  joined_at: string;
  user_count: number;
}

interface AccountDetails {
  id: string;
  name: string;
  plan_type: string;
  plan_status: string;
  monthly_limit_records: number;
  monthly_limit_storage_gb: number;
  monthly_limit_inboxes: number;
  current_usage_records: number;
  current_usage_storage_gb: number;
  current_usage_inboxes: number;
  billing_cycle_start: string;
  billing_cycle_end: string;
  created_at: string;
  updated_at: string;
  user_role: 'owner' | 'admin' | 'member';
}

interface ImpersonatedUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: 'admin' | 'user';
}

interface AccountContextType {
  // State
  user: User | null;
  accounts: Account[];
  currentAccount: Account | null;
  accountDetails: AccountDetails | null;
  accountUsers: AccountUser[];
  loading: boolean;
  error: string | null;

  // User Impersonation state
  isImpersonating: boolean;
  originalAdminUser: User | null;
  impersonatedUser: ImpersonatedUser | null;

  // Actions
  setCurrentAccount: (account: Account | null) => void;
  refreshAccounts: () => Promise<void>;
  refreshAccountDetails: () => Promise<void>;
  refreshAccountUsers: () => Promise<void>;

  // Account management
  updateAccountSettings: (accountId: string, settings: { name?: string; plan_type?: string }) => Promise<boolean>;
  inviteUserToAccount: (accountId: string, email: string, role: 'owner' | 'admin' | 'member') => Promise<void>;
  updateUserRole: (accountId: string, userId: string, role: 'owner' | 'admin' | 'member') => Promise<boolean>;
  removeUserFromAccount: (accountId: string, userId: string) => Promise<boolean>;

  // User impersonation
  startUserImpersonation: (targetUserId: string, targetUserEmail: string, targetUserName?: string, targetUserRole?: 'admin' | 'user') => Promise<void>;
  exitImpersonation: () => void;
  isSiteAdmin: () => Promise<boolean>;

  // Permissions
  canManageAccount: (role: 'owner' | 'admin' | 'member') => boolean;
  canManageUsers: () => boolean;
  canManageSettings: () => boolean;
  canManageIntegrations: () => boolean;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

interface AccountProviderProps {
  children: ReactNode;
}

export const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null);
  const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(null);
  const [accountUsers, setAccountUsers] = useState<AccountUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // User Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdminUser, setOriginalAdminUser] = useState<User | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);

  // Listen for auth changes
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Load user's accounts when authenticated
  useEffect(() => {
    if (user) {
      refreshAccounts();
    } else {
      setAccounts([]);
      setCurrentAccount(null);
      setAccountDetails(null);
      setAccountUsers([]);
      setLoading(false);
    }
  }, [user]);

  // Load account details when current account changes
  useEffect(() => {
    if (currentAccount) {
      refreshAccountDetails();
    } else {
      setAccountDetails(null);
      setAccountUsers([]);
    }
  }, [currentAccount]);

  const refreshAccounts = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      let accountsData: any[] = [];

      if (isImpersonating && impersonatedUser) {
        // Use impersonation endpoint
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        const token = (await supabase.auth.getSession()).data.session?.access_token;

        const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonatedUser.id}/accounts?currentUserId=${currentUser?.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch impersonated user accounts');
        }

        const responseData = await response.json();
        accountsData = responseData.accounts || [];
      } else {
        // Normal user account fetching
        const { data, error } = await supabase.rpc('get_user_accounts');
        if (error) throw error;
        accountsData = data || [];
      }

      // Map database response to match Account interface
      const mappedAccountsData = accountsData.map((account: any) => ({
        account_id: account.account_id,
        account_name: account.account_name,
        user_role: account.role, // Map 'role' field to 'user_role'
        plan_type: account.plan_type,
        plan_status: account.plan_status || 'active',
        is_account_active: account.is_account_active !== false,
        joined_at: account.joined_at,
        user_count: account.user_count || 0,
      }));

      console.log('Mapped accounts data:', mappedAccountsData);
      setAccounts(mappedAccountsData);

      // If no current account is set, set the first one
      if (!currentAccount && mappedAccountsData.length > 0) {
        setCurrentAccount(mappedAccountsData[0]);
      }

      // If current account is no longer available, clear it
      if (currentAccount && !mappedAccountsData.find((a: Account) => a.account_id === currentAccount.account_id)) {
        setCurrentAccount(mappedAccountsData.length > 0 ? mappedAccountsData[0] : null);
      }

    } catch (err: any) {
      console.error('Error loading accounts:', err);
      setError(err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const refreshAccountDetails = async () => {
    if (!currentAccount) return;

    try {
      let accountDetails: any = null;

      if (isImpersonating && impersonatedUser) {
        // Use impersonation endpoint
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        const token = (await supabase.auth.getSession()).data.session?.access_token;

        const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonatedUser.id}/account/${currentAccount.account_id}/details?currentUserId=${currentUser?.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch impersonated account details');
        }

        const responseData = await response.json();
        accountDetails = responseData.accountDetails;
      } else {
        // Normal account details fetching
        const { data, error } = await supabase.rpc('get_account_details', {
          account_uuid: currentAccount.account_id
        });

        if (error) throw error;
        accountDetails = data && data.length > 0 ? data[0] : null;
      }

      setAccountDetails(accountDetails);

    } catch (err: any) {
      console.error('Error loading account details:', err);
      setError(err.message || 'Failed to load account details');
    }
  };

  const refreshAccountUsers = async () => {
    if (!currentAccount) return;

    try {
      let accountUsersData: any[] = [];

      if (isImpersonating && impersonatedUser) {
        // Use impersonation endpoint
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        const token = (await supabase.auth.getSession()).data.session?.access_token;

        const response = await fetch(`${API_BASE_URL}/admin/impersonate/${impersonatedUser.id}/account/${currentAccount.account_id}/users?currentUserId=${currentUser?.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch impersonated account users');
        }

        const responseData = await response.json();
        accountUsersData = responseData.users || [];
      } else {
        // Normal account users fetching
        const { data, error } = await supabase.rpc('get_account_users', {
          account_uuid: currentAccount.account_id
        });

        if (error) throw error;
        accountUsersData = data || [];
      }

      setAccountUsers(accountUsersData);

    } catch (err: any) {
      console.error('Error loading account users:', err);
      setError(err.message || 'Failed to load account users');
    }
  };

  const updateAccountSettings = async (accountId: string, settings: { name?: string; plan_type?: string }) => {
    try {
      const { data, error } = await supabase.rpc('update_account_settings', {
        account_uuid: accountId,
        account_name: settings.name,
        new_plan_type: settings.plan_type
      });

      if (error) throw error;

      // Refresh accounts to get updated data
      await refreshAccounts();
      await refreshAccountDetails();

      return true;
    } catch (err: any) {
      console.error('Error updating account settings:', err);
      throw new Error(err.message || 'Failed to update account settings');
    }
  };

  const inviteUserToAccount = async (accountId: string, email: string, role: 'owner' | 'admin' | 'member') => {
    try {
      const { data, error } = await supabase.rpc('invite_user_to_account', {
        account_uuid: accountId,
        user_email: email,
        user_role: role
      });

      if (error) throw error;

      // Refresh account users
      await refreshAccountUsers();
      await refreshAccounts(); // Update user count

    } catch (err: any) {
      console.error('Error inviting user:', err);
      throw new Error(err.message || 'Failed to invite user');
    }
  };

  const updateUserRole = async (accountId: string, userId: string, role: 'owner' | 'admin' | 'member') => {
    try {
      const { data, error } = await supabase.rpc('update_account_user_role', {
        account_uuid: accountId,
        target_user_id: userId,
        new_role: role
      });

      if (error) throw error;

      // Refresh account users
      await refreshAccountUsers();

      return true;
    } catch (err: any) {
      console.error('Error updating user role:', err);
      throw new Error(err.message || 'Failed to update user role');
    }
  };

  const removeUserFromAccount = async (accountId: string, userId: string) => {
    try {
      const { data, error } = await supabase.rpc('remove_user_from_account', {
        account_uuid: accountId,
        target_user_id: userId
      });

      if (error) throw error;

      // Refresh account users and accounts
      await refreshAccountUsers();
      await refreshAccounts(); // Update user count

      return true;
    } catch (err: any) {
      console.error('Error removing user:', err);
      throw new Error(err.message || 'Failed to remove user');
    }
  };

  // Site admin check
  const isSiteAdmin = async () => {
    if (!user) return false;

    try {
      const { data, error } = await supabase.rpc('is_admin');
      return data === true;
    } catch (err) {
      console.error('Error checking admin status:', err);
      return false;
    }
  };

  // User impersonation functions
  const startUserImpersonation = async (
    targetUserId: string,
    targetUserEmail: string,
    targetUserName?: string,
    targetUserRole: 'admin' | 'user' = 'user'
  ) => {
    if (!user || !(await isSiteAdmin())) {
      throw new Error('Only site administrators can impersonate users');
    }

    // Don't allow impersonating other site admins
    if (targetUserRole === 'admin') {
      throw new Error('Cannot impersonate other site administrators');
    }

    try {
      setLoading(true);

      // Store original admin context
      setOriginalAdminUser(user);

      // Set up impersonated user
      const impersonated: ImpersonatedUser = {
        id: targetUserId,
        email: targetUserEmail,
        first_name: targetUserName?.split(' ')[0],
        last_name: targetUserName?.split(' ')[1],
        role: targetUserRole,
      };

      // Set impersonation state first
      setIsImpersonating(true);
      setImpersonatedUser(impersonated);

      // Clear current account data
      setAccounts([]);
      setCurrentAccount(null);
      setAccountDetails(null);
      setAccountUsers([]);

      // Simulate user change by creating a fake user object for the impersonated user
      const fakeUser = {
        ...user,
        id: targetUserId,
        email: targetUserEmail,
        user_metadata: {
          ...user.user_metadata,
          first_name: targetUserName?.split(' ')[0],
          last_name: targetUserName?.split(' ')[1],
          role: targetUserRole,
        }
      } as User;

      setUser(fakeUser);

      // Now fetch the impersonated user's accounts and set their default account
      // This happens after setting the impersonation state so refreshAccounts will use the correct endpoints
      await refreshAccounts();

      // After accounts are loaded, the first account should be automatically set as currentAccount
      // and then we can load its details and users

    } catch (err: any) {
      console.error('Error starting user impersonation:', err);
      throw new Error(err.message || 'Failed to start user impersonation');
    } finally {
      setLoading(false);
    }
  };

  const exitImpersonation = () => {
    if (!isImpersonating || !originalAdminUser) return;

    // Restore original admin user
    setUser(originalAdminUser);
    setIsImpersonating(false);
    setImpersonatedUser(null);
    setOriginalAdminUser(null);

    // Clear account data to force reload with admin context
    setAccounts([]);
    setCurrentAccount(null);
    setAccountDetails(null);
    setAccountUsers([]);

    // Reload admin accounts after state is cleared
    setTimeout(() => {
      refreshAccounts();
    }, 100);
  };

  // Permission helpers
  const canManageAccount = (requiredRole: 'owner' | 'admin' | 'member') => {
    if (!currentAccount) return false;

    const roleHierarchy = { owner: 3, admin: 2, member: 1 };
    return roleHierarchy[currentAccount.user_role] >= roleHierarchy[requiredRole];
  };

  const canManageUsers = () => {
    if (!currentAccount) return false;
    // Explicit check: owners and admins can manage users
    return currentAccount.user_role === 'owner' || currentAccount.user_role === 'admin';
  };

  const canManageSettings = () => {
    if (!currentAccount) return false;
    // Explicit check: owners and admins can manage settings
    return currentAccount.user_role === 'owner' || currentAccount.user_role === 'admin';
  };

  const canManageIntegrations = () => {
    if (!currentAccount) return false;
    // Explicit check: owners and admins can manage integrations
    return currentAccount.user_role === 'owner' || currentAccount.user_role === 'admin';
  };

  const value: AccountContextType = {
    // State
    user,
    accounts,
    currentAccount,
    accountDetails,
    accountUsers,
    loading,
    error,

    // User Impersonation state
    isImpersonating,
    originalAdminUser,
    impersonatedUser,

    // Actions
    setCurrentAccount,
    refreshAccounts,
    refreshAccountDetails,
    refreshAccountUsers,

    // Account management
    updateAccountSettings,
    inviteUserToAccount,
    updateUserRole,
    removeUserFromAccount,

    // User impersonation
    startUserImpersonation,
    exitImpersonation,
    isSiteAdmin,

    // Permissions
    canManageAccount,
    canManageUsers,
    canManageSettings,
    canManageIntegrations,
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
}; 